import React from "react";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../app/store/slices/uiSlice";

/**
 * Table Component - Matches Patient page design
 * Clean, modern table with soft shadows and borders
 */
export const Table = ({ children, className = "" }) => {
  const darkMode = useSelector(selectDarkMode);
  return (
    <div className={`rounded-2xl shadow-soft border overflow-hidden ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"} ${className}`}>
      <div className="overflow-x-auto">
        <table className={`min-w-full divide-y ${darkMode ? "divide-gray-700" : "divide-gray-200"}`}>{children}</table>
      </div>
    </div>
  );
};

/**
 * TableHeader Component
 */
export const TableHead = ({ children, className = "" }) => {
  const darkMode = useSelector(selectDarkMode);
  return <thead className={`font-semibold ${darkMode ? "bg-gray-900" : "bg-[#e9f1ff]"} ${className}`}>{children}</thead>;
};

/**
 * TableHeaderCell Component
 */
export const TableHeaderCell = ({ children, className = "", onClick }) => {
  const darkMode = useSelector(selectDarkMode);
  return (
    <th className={`px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? "bg-gray-900 text-gray-300" : "bg-[#e9f1ff] text-[#232526]"} ${onClick ? `cursor-pointer ${darkMode ? "hover:bg-gray-800" : "hover:bg-blue-100"}` : ""} ${className}`} onClick={onClick}>
      {children}
    </th>
  );
};

/**
 * TableBody Component
 */
export const TableBody = ({ children, className = "" }) => {
  const darkMode = useSelector(selectDarkMode);
  return <tbody className={`divide-y ${darkMode ? "bg-gray-800 divide-gray-700" : "bg-white divide-gray-200"} ${className}`}>{children}</tbody>;
};

/**
 * TableRow Component
 */
export const TableRow = ({ children, className = "", onClick }) => {
  const darkMode = useSelector(selectDarkMode);
  return (
    <tr className={`transition-colors ${darkMode ? "text-gray-200 hover:bg-gray-700/50" : "text-[#232526] hover:bg-gray-50"} ${onClick ? "cursor-pointer" : ""} ${className}`} onClick={onClick}>
      {children}
    </tr>
  );
};

/**
 * TableCell Component
 */
export const TableCell = ({ children, className = "" }) => {
  const darkMode = useSelector(selectDarkMode);
  return <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? "text-gray-200" : "text-[#232526]"} ${className}`}>{children}</td>;
};

/**
 * EmptyState Component for tables
 */
export const TableEmptyState = ({ icon: Icon, title = "No data found", message = "Get started by adding a new item", action, actionLabel = "Add New" }) => {
  const darkMode = useSelector(selectDarkMode);
  return (
    <tr>
      <td colSpan="100%" className="px-6 py-12">
        <div className="text-center">
          {Icon && <Icon className={`mx-auto h-12 w-12 ${darkMode ? "text-gray-500" : "text-gray-400"}`} />}
          <h3 className={`mt-2 text-sm font-normal ${darkMode ? "text-gray-300" : "text-gray-900"}`}>{title}</h3>
          <p className={`mt-1 text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{message}</p>
          {action && (
            <div className="mt-6">
              <button onClick={action} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-[linear-gradient(180deg,#4A9CFF_0%,#4057FE_100%)] hover:opacity-90 transition-opacity">
                {actionLabel}
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

/**
 * ActionButton Component - Matches Patient page design
 */
export const TableActionButton = ({ icon: Icon, onClick, variant = "primary", disabled = false, tooltip = "" }) => {
  const darkMode = useSelector(selectDarkMode);
  const variants = {
    primary: darkMode ? "bg-blue-900/30 text-blue-400" : "bg-[#4a9cff30] text-[#4057FE]",
    success: darkMode ? "bg-green-900/30 text-green-400" : "bg-[#13B6011A] text-[#13B601]",
    warning: darkMode ? "bg-yellow-900/30 text-yellow-400" : "bg-[#F0E93B1A] text-[#F0E93B]",
    danger: darkMode ? "bg-red-900/30 text-red-400" : "bg-[#FF3B3012] text-[#FF3B30]",
  };

  return (
    <button onClick={onClick} disabled={disabled} title={tooltip} className={`flex items-center justify-center w-[24px] h-[24px] rounded transition-opacity ${variants[variant]} ${disabled ? "opacity-50 cursor-not-allowed" : "hover:opacity-80"}`}>
      {Icon && <Icon className="h-4 w-4" />}
    </button>
  );
};

/**
 * PrimaryButton Component - Gradient button matching Patient page
 */
export const PrimaryButton = ({ children, icon: Icon, onClick, disabled = false, className = "" }) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center px-4 py-2 rounded-xl text-white font-semibold bg-[linear-gradient(180deg,#4A9CFF_0%,#4057FE_100%)] hover:opacity-90 transition-opacity shadow-sm ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
    >
      {Icon && <Icon className="h-4 w-4 mr-2" />}
      {children}
    </button>
  );
};

/**
 * SecondaryButton Component
 */
export const SecondaryButton = ({ children, icon: Icon, onClick, disabled = false, className = "" }) => {
  const darkMode = useSelector(selectDarkMode);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center px-4 py-2 rounded-xl border font-semibold transition-colors ${
        darkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-[#F0EFEF] text-[#232526] hover:bg-gray-50"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
    >
      {Icon && <Icon className="h-4 w-4 mr-2" />}
      {children}
    </button>
  );
};

/**
 * ToggleSwitch Component - Active/Inactive toggle
 */
export const ToggleSwitch = ({ checked, onChange, label, activeLabel = "Active", inactiveLabel = "Inactive" }) => {
  const darkMode = useSelector(selectDarkMode);
  return (
    <div className="flex items-center space-x-2">
      <button onClick={onChange} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-green-500" : darkMode ? "bg-gray-600" : "bg-gray-300"}`}>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
      </button>
      {label && <span className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}>{checked ? activeLabel : inactiveLabel}</span>}
    </div>
  );
};

/**
 * SearchContainer Component - Matches Patient page design
 */
export const SearchContainer = ({ children, className = "" }) => {
  const darkMode = useSelector(selectDarkMode);
  return <div className={`rounded-xl p-4 shadow-soft border ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"} ${className}`}>{children}</div>;
};

/**
 * ResultsSummary Component
 */
export const ResultsSummary = ({ start, end, total, label = "item" }) => {
  const darkMode = useSelector(selectDarkMode);
  return (
    <div className={`text-sm my-6 ${darkMode ? "text-gray-400" : "text-[#74777F]"}`}>
      Showing {start} to {end} of {total} {label}
    </div>
  );
};

export default Table;
